import { FC } from 'react';
import {
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  UserAddOutlined,
  UserOutlined,
} from '@ant-design/icons';

import {
  CLIENT_TYPE_TABS,
  ClientType,
  ListenerFullRow,
} from '../CourseEnrollment.const';
import { ClientForm } from '../CourseEnrollment.types';
import css from '../CourseEnrollment.module.css';

interface Props {
  courseTitle: string;
  courseFormat?: string;
  courseDuration?: string;
  clientType: ClientType;
  form: ClientForm;
  listeners: ListenerFullRow[];
  onAdd: () => void;
  onAddSelf: () => void;
  onAddFromContract: () => void;
  onAddFromPrevious: () => void;
  onEdit: (l: ListenerFullRow) => void;
  onRemove: (id: string) => void;
}

const CLIENT_TYPE_FULL_LABEL: Record<ClientType, string> = {
  legal: 'Юридическое лицо',
  ip: 'ИП',
  individual: 'Физлицо',
};

const ListenersStep: FC<Props> = ({
  courseTitle,
  courseFormat,
  courseDuration,
  clientType,
  form,
  listeners,
  onAdd,
  onAddSelf,
  onAddFromContract,
  onAddFromPrevious,
  onEdit,
  onRemove,
}) => {
  const clientTypeLabel = CLIENT_TYPE_FULL_LABEL[clientType] ?? clientType;

  return (
    <>
      <div className={css.ContractSummary}>
        <div className={css.ContractSummaryGrid}>
          <div className={css.ContractSummaryItem}>
            <span className={css.ContractSummaryItemLabel}>Курс</span>
            <span className={css.ContractSummaryItemValue}>
              {courseTitle}
            </span>
          </div>
          <div className={css.ContractSummaryItem}>
            <span className={css.ContractSummaryItemLabel}>Клиент</span>
            <span className={css.ContractSummaryItemValue}>
              {form.organization || '—'}
            </span>
          </div>
          <div className={css.ContractSummaryItem}>
            <span className={css.ContractSummaryItemLabel}>Формат</span>
            <span className={css.ContractSummaryItemValue}>
              {courseFormat ?? '—'}
            </span>
          </div>
          <div className={css.ContractSummaryItem}>
            <span className={css.ContractSummaryItemLabel}>Тип клиента</span>
            <span className={css.ContractSummaryItemValue}>
              {clientTypeLabel}
            </span>
          </div>
          <div className={css.ContractSummaryItem}>
            <span className={css.ContractSummaryItemLabel}>Длительность</span>
            <span className={css.ContractSummaryItemValue}>
              {courseDuration ?? '—'}
            </span>
          </div>
          <div className={css.ContractSummaryItem}>
            <span className={css.ContractSummaryItemLabel}>УНП</span>
            <span className={css.ContractSummaryItemValue}>
              {form.unp || '—'}
            </span>
          </div>
        </div>
      </div>

      <h2 className={css.SectionHeading}>Слушатели</h2>

      <div className={css.ListenersActions}>
        <button type='button' className={css.ListenerActionBtn} onClick={onAdd}>
          <PlusOutlined /> Добавить слушателя
        </button>
        <button type='button' className={css.ListenerActionBtn} onClick={onAddSelf}>
          <UserOutlined /> Добавить себя
        </button>
        <button
          type='button'
          className={`${css.ListenerActionBtn} ${css.ListenerActionBtnAlt}`}
          onClick={onAddFromContract}
        >
          <FileTextOutlined /> Добавить данные из договора
        </button>
        <button type='button' className={css.ListenerActionBtn} onClick={onAddFromPrevious}>
          <UserAddOutlined /> Добавить из ранее введенных
        </button>
      </div>

      <div className={css.ListenersHint}>
        <InfoCircleOutlined />
        Добавьте минимум одного слушателя для продолжения оформления договора.
      </div>

      <div className={css.TableWrapper}>
        <table className={css.Table}>
          <thead>
            <tr>
              <th>ФИО на русском</th>
              <th>ФИО на белорусском</th>
              <th>Адрес</th>
              <th>Паспортные данные</th>
              <th>E-mail для доступа с другого аккаунта</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {listeners.map((l) => (
              <tr key={l.id}>
                <td>{l.fullNameRu || '—'}</td>
                <td>{l.fullNameBy || '—'}</td>
                <td>{l.address || '—'}</td>
                <td>
                  <div className={css.PassportCell}>
                    <span>{l.passport || '—'}</span>
                    {(l.passportIssueDate || l.passportIssuer) && (
                      <span>
                        {l.passportIssueDate}
                        {l.passportIssueDate && l.passportIssuer ? ', ' : ''}
                        {l.passportIssuer}
                      </span>
                    )}
                  </div>
                </td>
                <td>{l.externalEmail || '—'}</td>
                <td>
                  <div className={css.RowActions}>
                    <button
                      type='button'
                      className={css.RowActionBtn}
                      onClick={() => onEdit(l)}
                    >
                      <EditOutlined /> Изменить
                    </button>
                    <button
                      type='button'
                      className={css.RowActionBtn}
                      data-variant='danger'
                      onClick={() => onRemove(l.id)}
                    >
                      <DeleteOutlined /> Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ListenersStep;
